import { useNavigate, useLocation } from "react-router-dom";
import { VerifyMFA } from "@/components/mfa/VerifyMFA";
import { useAuth } from "@/contexts/AuthContext";
import { useMFA } from "@/contexts/MFAContext";
import { useEffect } from "react";
import { logger } from "@/lib/logger";

const MFAVerifyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { hasEnrolledTOTP, isLoadingMFAStatus, currentAAL } = useMFA();

  // Get the intended destination from state, or default to dashboard
  const from = (location.state as { from?: string })?.from || "/dashboard";

  // If user doesn't have MFA enrolled, redirect to setup
  useEffect(() => {
    if (!isLoadingMFAStatus && !hasEnrolledTOTP) {
      logger.debug("MFAVerifyPage: User has no MFA enrolled, redirecting to setup");
      navigate("/mfa/setup", { replace: true });
    }
  }, [hasEnrolledTOTP, isLoadingMFAStatus, navigate]);

  // If user is already at AAL2, redirect to intended destination
  useEffect(() => {
    if (!isLoadingMFAStatus && currentAAL === "aal2") {
      logger.debug("MFAVerifyPage: User already at AAL2, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [currentAAL, isLoadingMFAStatus, navigate, from]);

  const handleVerified = () => {
    logger.debug("MFAVerifyPage: MFA verification complete, redirecting to:", from);
    navigate(from, { replace: true });
  };

  const handleCancel = async () => {
    // Sign out and redirect to auth page
    logger.debug("MFAVerifyPage: User cancelled MFA verification");
    await signOut();
    navigate("/auth", { replace: true });
  };

  if (!user) {
    return null; // PrivateRoute should handle this
  }

  if (isLoadingMFAStatus) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-grow container mx-auto px-4 py-12">
        <header className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Verify Your Identity
          </h1>
          <p className="mt-3 text-lg text-muted-foreground max-w-xl mx-auto">
            Please enter your authentication code to continue.
          </p>
        </header>

        <VerifyMFA onVerified={handleVerified} onCancel={handleCancel} />
      </div>
    </div>
  );
};

export default MFAVerifyPage;
